import { useEffect } from "react";
import { repo } from "./lib/repo.js";
import { useRepoVersion, useRoute } from "./lib/hooks.js";
import { syncAllGroups, syncGroup } from "./lib/relaySync.js";
import { Onboard } from "./screens/Onboard.js";
import { Walkthrough } from "./screens/Walkthrough.js";
import { Home } from "./screens/Home.js";
import { Join } from "./screens/Join.js";
import { Group } from "./screens/Group.js";

export default function App() {
  useRepoVersion(); // re-renders on repo.bump(), including the one init() fires
  const route = useRoute();

  useEffect(() => {
    void repo.init().then(() => void syncAllGroups());
  }, []);

  // Auto-sync shortly after a local change (expense, payment, edit…). Debounced
  // per group so a burst of ops becomes one relay round-trip; syncGroup's own
  // in-flight guard makes an overlap with a running sync harmless.
  useEffect(() => {
    const timers = new Map<string, ReturnType<typeof setTimeout>>();
    repo.onLocalOp = (groupId) => {
      clearTimeout(timers.get(groupId));
      timers.set(
        groupId,
        setTimeout(() => {
          timers.delete(groupId);
          void syncGroup(groupId);
        }, 1_200),
      );
    };
    return () => {
      repo.onLocalOp = null;
      for (const t of timers.values()) clearTimeout(t);
    };
  }, []);

  // Opportunistic sync whenever the app returns to the foreground - the core
  // sync model on phones (no background execution for web apps on iOS).
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible" && repo.ready) void syncAllGroups();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
  }, []);

  if (!repo.ready) {
    return (
      <div className="page center">
        <h1 className="brand">PaisaSplit</h1>
        <p className="muted">opening your ledgers…</p>
      </div>
    );
  }
  if (!repo.identity) return <Onboard />;
  if (route.name === "join") return <Join fragment={route.fragment} />;
  if (route.name === "group") return <Group id={route.id} tab={route.tab} />;
  // First-run intro, only when landing with no other context (not mid-join or
  // inside a group) so a just-joined user is not interrupted by it.
  if (!repo.seenIntro) return <Walkthrough onDone={() => void repo.markIntroSeen()} />;
  return <Home />;
}
